// Generic authenticated API client
// Domain-specific methods belong in consuming projects, not here.

import { authState } from './auth.js';

/**
 * Get the backend API base URL
 * @returns {string} Backend base URL
 */
export function getBackendUrl() {
    return import.meta.env.VITE_API_URL || '';
}

/**
 * Get full URL for an image path
 * @param {string|null|undefined} url - The URL or path to transform
 * @returns {string|null} Full URL for the image
 */
export function getImageUrl(url) {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return getBackendUrl() + url;
}

/**
 * Authenticated API client with standard HTTP methods.
 * Automatically attaches Bearer token from authState.
 */
export class ApiClient {
    constructor() {
        /** @type {string} */
        this.baseUrl = getBackendUrl();
    }

    /**
     * Make an authenticated API request
     * @param {string} path - API endpoint path
     * @param {RequestInit} [options] - Fetch options
     * @returns {Promise<Response>}
     */
    async fetch(path, options = {}) {
        const url = `${this.baseUrl}${path}`;
        const token = await authState.getAccessToken();

        const headers = new Headers(options.headers || {});
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
        if (!headers.has('Content-Type') && options.body && typeof options.body === 'string') {
            headers.set('Content-Type', 'application/json');
        }

        return fetch(url, {
            ...options,
            headers,
        });
    }

    /**
     * Parse the response body as JSON and surface a clean error if it isn't
     * JSON at all. Without this, a stray HTML body (from a proxy / CF error
     * page / SPA fallback) bubbles up as "Unexpected token '<', '<!DOCTYPE'…"
     * which is gibberish to users.
     * @param {Response} response
     * @returns {Promise<*>}
     */
    async _parseJsonOrThrow(response) {
        try {
            return await response.json();
        } catch {
            throw new Error(`API error: ${response.status} ${response.statusText} (non-JSON response)`);
        }
    }

    /**
     * Make a GET request and return JSON
     * @template T
     * @param {string} path - API endpoint path
     * @returns {Promise<T>}
     */
    async get(path) {
        const response = await this.fetch(path);
        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }
        return this._parseJsonOrThrow(response);
    }

    /**
     * Make a POST request and return JSON
     * @template T
     * @param {string} path - API endpoint path
     * @param {object} [data] - Request body
     * @returns {Promise<T>}
     */
    async post(path, data) {
        /** @type {RequestInit} */
        const options = { method: 'POST' };
        if (data) options.body = JSON.stringify(data);
        const response = await this.fetch(path, options);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `API error: ${response.status} ${response.statusText}`);
        }
        return this._parseJsonOrThrow(response);
    }

    /**
     * Make a PUT request and return JSON
     * @template T
     * @param {string} path - API endpoint path
     * @param {object} [data] - Request body
     * @returns {Promise<T>}
     */
    async put(path, data) {
        /** @type {RequestInit} */
        const options = { method: 'PUT' };
        if (data) options.body = JSON.stringify(data);
        const response = await this.fetch(path, options);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `API error: ${response.status} ${response.statusText}`);
        }
        return this._parseJsonOrThrow(response);
    }

    /**
     * Make a PATCH request and return JSON
     * @template T
     * @param {string} path - API endpoint path
     * @param {object} [data] - Request body
     * @returns {Promise<T>}
     */
    async patch(path, data) {
        /** @type {RequestInit} */
        const options = { method: 'PATCH' };
        if (data) options.body = JSON.stringify(data);
        const response = await this.fetch(path, options);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `API error: ${response.status} ${response.statusText}`);
        }
        return this._parseJsonOrThrow(response);
    }

    /**
     * Make a DELETE request
     * @param {string} path - API endpoint path
     * @returns {Promise<void>}
     */
    async delete(path) {
        const response = await this.fetch(path, {
            method: 'DELETE',
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `API error: ${response.status} ${response.statusText}`);
        }
    }

    /**
     * Upload a file
     * @param {string} path - API endpoint path
     * @param {FormData} formData - Form data with file
     * @returns {Promise<object>}
     */
    async upload(path, formData) {
        const url = `${this.baseUrl}${path}`;
        const token = await authState.getAccessToken();

        const headers = new Headers();
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }

        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: formData,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Upload error: ${response.status} ${response.statusText}`);
        }

        return response.json();
    }

    /**
     * Get backend URL for direct use
     * @returns {string}
     */
    getBackendUrl() {
        return this.baseUrl;
    }

    /**
     * Get full URL for an image path
     * @param {string|null|undefined} url - The URL or path to transform
     * @returns {string|null}
     */
    getImageUrl(url) {
        return getImageUrl(url);
    }
}

// Singleton instance
export const api = new ApiClient();
